// ============================================
// File: src/components/teams/utils/moduleUtils.js
// Helpers to safely read module flags & apply defaults (communications subtree, etc.)
// ============================================
export const readAtPath = (obj, path = []) =>
  path.reduce((o, k) => (o && o[k] != null ? o[k] : undefined), obj);

export const enabledAtPath = (obj, path = []) => !!readAtPath(obj, path)?.enabled;

export const EVENT_MODULES = Object.freeze([
  { path: ['events'], label: 'Events', description: 'Schedule team events and display them in a member calendar.' },
  { path: ['events', 'bookingV2'], label: 'Extended event booking', description: 'Enable configurable forms, ticket types, extras and protected booking answers for events.' },
  { path: ['courses'], label: 'Courses', description: 'Run multi-session courses with ticketing, forms, waitlists, attendance, and conversion.' },
]);

export const CLUB_SETUP_MODULES = Object.freeze([
  { path: ['links'], label: 'Public Links', description: 'Showcase useful public links like registration forms or websites.' },
  { path: ['documents'], label: 'Documents', description: 'Manage club documents, useful files, and join requirements.' },
  { path: ['branding'], label: 'Branding', description: "Customise your club's colours and visual identity." },
]);

export const PEOPLE_PARTICIPATION_MODULES = Object.freeze([
  {
    path: ['profile'],
    label: 'Member Profile Fields',
    description: 'Collect club-specific member data and qualifications with certificate evidence and expiry dates.',
  },
  {
    path: ['mentoring'],
    label: 'Mentoring',
    description: 'Manage matching and pairings, with an optional guided journey from kick-off to reflection.',
  },
]);

export const REPORTING_MODULES = Object.freeze([
  {
    path: ['reporting'],
    label: 'Reporting',
    description: 'Build shared operational reports and dashboards from enabled club modules.',
  },
  {
    path: ['demographics'],
    label: 'Demographic Reporting',
    description: 'Collect private member responses and give full admins disclosure-protected aggregate snapshots.',
  },
]);

export const MEMBERSHIP_PAYMENT_MODULES = Object.freeze([
  { path: ['membershipReconciliation'], label: 'Treasurer Reconciliation', description: 'Run optional payment rounds, assign members, and reconcile external membership payments.' },
  { path: ['boltons'], label: 'Bolt Ons', description: 'Add additional subscriptions members can pay for once signed up.' },
  { path: ['discounts'], label: 'Discounts', description: 'Create membership discount codes, personal offers, and redemption reporting.' },
  { path: ['waitlist'], label: 'Waitlist', description: 'Enable the admin waitlist tools. Control whether joiners enter the queue from the waitlist card.' },
]);

export const ACCESS_ROLE_MODULES = Object.freeze([
  { path: ['permissions'], label: 'Admin Permissions', description: 'Create limited admin roles and assign feature-level access to members.' },
]);

export const COMMUNICATION_MODULES = Object.freeze([
  {
    path: ['communications', 'messages'],
    label: 'Messaging',
    description: 'Allow admins to message members, and members to message admins directly within the platform.',
    legacyPath: ['messages'],
  },
  {
    path: ['communications', 'inboxBroadcasts'],
    label: 'Inbox Broadcasts',
    description: 'Send one in-app message to many members privately. Requires Messaging to stay enabled.',
  },
  {
    path: ['communications', 'boards'],
    label: 'Message Boards',
    description: 'Enable club discussion boards with topics, replies, and moderation tools.',
  },
  {
    path: ['communications', 'email'],
    label: 'Email Members',
    description: 'Send tracked emails to individual members, selected tags, or the whole club, including open and click metrics.',
  },
]);

export const BESPOKE_MODULE_ITEMS = Object.freeze({
  iamRoadSmart: Object.freeze([
    {
      path: ['iamRoadSmart', 'rideReports'],
      label: 'Ride Reports',
      description: 'Let tagged observers write structured, versioned ride reports for other group members.',
    },
    {
      path: ['iamRoadSmart', 'rideDeclarations'],
      label: 'Ride Declarations',
      description: 'Digitise versioned pre-ride declarations, signatures, live proof, and roadside checks.',
    },
  ]),
  rowing: Object.freeze([
    {
      path: ['rowing', 'boats'],
      label: 'Club Boats',
      description: 'Track club-owned rowing boats, statuses, tag restrictions, and booking availability.',
    },
    {
      path: ['rowing', 'boatBooking'],
      label: 'Boat Booking',
      description: 'Let members reserve boats and oars by time slot. Supports restrictions, maintenance lockouts, and crew assignments.',
    },
    {
      path: ['rowing', 'outings'],
      label: 'Outings',
      description: 'Turn RSVP signups into draft crews and submit them into the shared rowing allocation flow.',
    },
    {
      path: ['rowing', 'learnToRow'],
      label: 'Learn to Row',
      description: 'Run beginner course cohorts with capped places, waitlists, calendar sessions, and post-course conversion.',
    },
    {
      path: ['rowing', 'boatMaintenance'],
      label: 'Boat Maintenance',
      description: 'Track rowing boat maintenance tickets, updates, and out-of-service state.',
    },
  ]),
  sailing: Object.freeze([
    {
      path: ['sailing', 'learnToSail'],
      label: 'Learn to Sail',
      description: 'Run beginner sailing course cohorts with capped places, waitlists, calendar sessions, and post-course conversion.',
    },
    {
      path: ['sailing', 'dutyRoster'],
      label: 'Duty Roster',
      description: 'Plan sailing duties from event roles, generate recurring role slots, and balance assignments by season.',
    },
    {
      path: ['sailing', 'moorings'],
      label: 'Moorings',
      description: 'Manage mooring maps, allocations, waiting lists, maintenance notes, and linked mooring fee bolt-ons.',
    },
    {
      path: ['sailing', 'boats'],
      label: 'Club Boats',
      description: 'Track club-owned sailing boats, statuses, tag restrictions, and mooring links.',
    },
    {
      path: ['sailing', 'boatBooking'],
      label: 'Sailing Boat Booking',
      description: 'Let members request club boats by time slot and manage the shared sailing allocation flow.',
    },
    {
      path: ['sailing', 'outings'],
      label: 'Club-led Outings',
      description: 'Admin-led sailing sessions that turn RSVP signups into draft crews and boat allocation.',
    },
    {
      path: ['sailing', 'crewFinder'],
      label: 'Crew Finder',
      description: 'Help skippers find crew and members share crew availability using club boats, moorings, and events.',
    },
    {
      path: ['sailing', 'boatMaintenance'],
      label: 'Boat Maintenance',
      description: 'Track sailing boat maintenance tickets, updates, and out-of-service state.',
    },
  ]),
});

function hasBooleanEnabled(value) {
  return typeof value?.enabled === 'boolean';
}

export function isSailingBoatBookingEnabled(modules = {}) {
  const sailing = modules?.sailing || {};
  if (hasBooleanEnabled(sailing?.boatBooking)) return sailing.boatBooking.enabled === true;
  return sailing?.boats?.memberBookingEnabled === true;
}

export function isSailingOutingsEnabled(modules = {}) {
  const sailing = modules?.sailing || {};
  if (hasBooleanEnabled(sailing?.outings)) return sailing.outings.enabled === true;
  return sailing?.boats?.outingsEnabled === true;
}

export function isRowingFleetEnabled(modules = {}) {
  const rowing = modules?.rowing || {};
  if (hasBooleanEnabled(rowing?.boats)) return rowing.boats.enabled === true;
  return rowing?.boatBooking?.enabled === true;
}

export function isRowingBoatBookingEnabled(modules = {}) {
  return modules?.rowing?.boatBooking?.enabled === true;
}

export function isRowingOutingsEnabled(modules = {}) {
  return modules?.rowing?.outings?.enabled === true;
}

export function isRowingBoatMaintenanceEnabled(modules = {}) {
  return modules?.rowing?.boatMaintenance?.enabled === true;
}

export function isSailingBoatMaintenanceEnabled(modules = {}) {
  return modules?.sailing?.boatMaintenance?.enabled === true;
}

export function getSailingRules(team = {}) {
  return {
    ...(team?.sailing?.boats?.rules || {}),
    ...(team?.sailing?.rules || {}),
  };
}

export function normalizeSailingCrewFinderSettings(settings = {}) {
  const postingMode = ['admins_only', 'all_members', 'tagged_members'].includes(String(settings?.postingMode || 'admins_only'))
    ? String(settings.postingMode || 'admins_only')
    : 'admins_only';
  return {
    enabled: settings?.enabled === true,
    postingMode,
    postingTagNames: Array.from(new Set(
      (Array.isArray(settings?.postingTagNames) ? settings.postingTagNames : [])
        .map((tagName) => String(tagName || '').trim())
        .filter(Boolean)
    )),
    allowCrewNeededPosts: settings?.allowCrewNeededPosts !== false,
    allowAvailabilityPosts: settings?.allowAvailabilityPosts !== false,
  };
}

export function isSailingCrewFinderEnabled(modules = {}) {
  return modules?.sailing?.crewFinder?.enabled === true;
}

export function getModuleSections(eligibleBespokeGroups = []) {
  return [
    { key: 'events', title: 'Events & Calendar', items: EVENT_MODULES },
    { key: 'clubSetup', title: 'Club Setup', items: CLUB_SETUP_MODULES },
    { key: 'peopleParticipation', title: 'People & Participation', items: PEOPLE_PARTICIPATION_MODULES },
    { key: 'membershipPayments', title: 'Membership & Payments', items: MEMBERSHIP_PAYMENT_MODULES },
    { key: 'reporting', title: 'Reporting', items: REPORTING_MODULES },
    { key: 'accessRoles', title: 'Access & Roles', items: ACCESS_ROLE_MODULES },
    { key: 'communication', title: 'Communication', items: COMMUNICATION_MODULES },
    ...eligibleBespokeGroups.map((group) => ({
      key: group.key,
      title: group.title,
      items: BESPOKE_MODULE_ITEMS[group.key] || [],
    })),
  ];
}

export function withModuleDefaults(modules = {}) {
  const rowing = modules?.rowing || {};
  const sailing = modules?.sailing || {};
  return {
    ...modules,
    events: { enabled: false, ...(modules?.events || {}), bookingV2: { enabled: false, ...(modules?.events?.bookingV2 || {}) } },
    courses: {
      enabled: false,
      protectedAnswerRetentionDays: 30,
      restrictActiveCourseCards: false,
      activeCourseVisibleCardIds: [],
      ...(modules?.courses || {}),
    },
    links: { enabled: false, ...(modules?.links || {}) },
    documents: { enabled: false, ...(modules?.documents || {}) },
    branding: { enabled: false, ...(modules?.branding || {}) },
    profile: { enabled: false, ...(modules?.profile || {}) },
    demographics: { enabled: false, ...(modules?.demographics || {}) },
    mentoring: {
      enabled: false,
      allowMemberApplications: true,
      programmeMode: 'matching_only',
      allowUnder18s: false,
      defaultCheckInCadenceDays: 28,
      programmeLengthWeeks: 24,
      ...(modules?.mentoring || {}),
    },
    boltons: { enabled: false, ...(modules?.boltons || {}) },
    discounts: { enabled: false, ...(modules?.discounts || {}) },
    membershipReconciliation: { enabled: false, ...(modules?.membershipReconciliation || {}) },
    permissions: { enabled: false, ...(modules?.permissions || {}) },
    reporting: { enabled: false, ...(modules?.reporting || {}) },
    waitlist: {
      enabled: false,
      joinRoutingEnabled: false,
      ...(modules?.waitlist || {}),
    },
    communications: {
      messages: { enabled: false },
      email: { enabled: false },
      inboxBroadcasts: { enabled: false },
      boards: { enabled: false },
      ...(modules?.communications || {}),
    },
    iamRoadSmart: {
      ...(modules?.iamRoadSmart || {}),
      rideReports: {
        enabled: false,
        observerTagNames: [],
        oversightMode: 'review_tracking',
        ...(modules?.iamRoadSmart?.rideReports || {}),
      },
      rideDeclarations: {
        enabled: false,
        checkerTagNames: [],
        membershipNumberProfileFieldId: '',
        ...(modules?.iamRoadSmart?.rideDeclarations || {}),
      },
    },
    rowing: {
      ...rowing,
      boats: {
        ...(rowing?.boats || {}),
        enabled: hasBooleanEnabled(rowing?.boats)
          ? rowing.boats.enabled === true
          : rowing?.boatBooking?.enabled === true,
      },
      boatBooking: { enabled: false, ...(rowing?.boatBooking || {}) },
      outings: { enabled: false, ...(rowing?.outings || {}) },
      boatMaintenance: {
        enabled: false,
        allowMemberReports: false,
        allowMemberTicketViewing: false,
        ...(rowing?.boatMaintenance || {}),
      },
      learnToRow: {
        enabled: false,
        waitlistOpen: false,
        memberNestFeeHandling: 'add',
        stripeFeeHandling: 'add',
        ...(rowing?.learnToRow || {}),
      },
    },
    sailing: {
      ...sailing,
      learnToSail: {
        enabled: false,
        waitlistOpen: false,
        memberNestFeeHandling: 'add',
        stripeFeeHandling: 'add',
        ...(sailing?.learnToSail || {}),
      },
      dutyRoster: {
        enabled: false,
        requiresAdminApprovalForSwaps: true,
        ...(sailing?.dutyRoster || {}),
      },
      boats: {
        enabled: false,
        showClubBoatNamesOnMooringMap: false,
        ...(sailing?.boats || {}),
      },
      boatBooking: {
        ...(sailing?.boatBooking || {}),
        enabled: hasBooleanEnabled(sailing?.boatBooking)
          ? sailing.boatBooking.enabled === true
          : sailing?.boats?.memberBookingEnabled === true,
      },
      outings: {
        ...(sailing?.outings || {}),
        enabled: hasBooleanEnabled(sailing?.outings)
          ? sailing.outings.enabled === true
          : sailing?.boats?.outingsEnabled === true,
      },
      crewFinder: normalizeSailingCrewFinderSettings(sailing?.crewFinder || {}),
      boatMaintenance: {
        enabled: false,
        allowMemberReports: false,
        allowMemberTicketViewing: false,
        ...(sailing?.boatMaintenance || {}),
      },
      moorings: {
        enabled: false,
        memberVisibility: 'admin_only',
        showMemberMap: false,
        allowMemberWaitlistRequests: false,
        ...(sailing?.moorings || {}),
        payment: {
          enabled: true,
          baselineCalculation: 'flat',
          cadenceMode: 'annual',
          stripeFeeHandling: 'deduct',
          areaPricingMode: 'fixed',
          flatPrices: {
            member: { monthlyAmount: 0, annualAmount: 0 },
            non_member: { monthlyAmount: 0, annualAmount: 0 },
          },
          lengthRates: {
            member: { monthlyAmountPerMetre: 0, annualAmountPerMetre: 0 },
            non_member: { monthlyAmountPerMetre: 0, annualAmountPerMetre: 0 },
          },
          areaPrices: {},
          areaRates: {},
          boatClasses: [],
          boatClassIds: [],
          ...(sailing?.moorings?.payment || {}),
        },
      },
    },
  };
}
